export { ModalViewport } from "./modal-viewport";
import * as React from "react";

// Keep forms, their submit buttons and all existing callbacks in the same tree.
function flatten(children) {
  return React.Children.toArray(children).flatMap(child =>
    React.isValidElement(child) && child.type === React.Fragment
      ? flatten(child.props.children) : [child]);
}
function slot(child, key) { return React.isValidElement(child) && (child.props[key] || (key === 'data-modal-footer' && child.type?.displayName === 'DialogFooter')); }
function sections(children) {
  const nodes = flatten(children);
  const header = nodes.filter(n => slot(n, 'data-modal-header'));
  const footer = nodes.filter(n => slot(n, 'data-modal-footer'));
  const body = nodes.filter(n => !slot(n, 'data-modal-header') && !slot(n, 'data-modal-footer'));
  const form = body.find(n => React.isValidElement(n) && n.type === 'form');
  if (form && body.filter(n => typeof n !== 'string' || n.trim()).length === 1) {
    const fields = flatten(form.props.children);
    const actions = fields.filter(n => slot(n, 'data-modal-footer'));
    return <>{header}{React.cloneElement(form, {
      className: `${form.props.className || ''} app-modal-form`,
      children: <><div className="app-modal-scroll app-modal-fields">{fields.filter(n => !slot(n, 'data-modal-footer'))}</div>{actions}</>
    })}{footer}</>;
  }
  return <>{header}<div className="app-modal-scroll">{body}</div>{footer}</>;
}
let locks = 0;
let previousOverflow;
export function ModalLayout({ children }) {
  const anchor = React.useRef(null);
  React.useEffect(() => {
    const panel = anchor.current.parentElement;
    const previousFocus = document.activeElement;
    const oldRole = panel.getAttribute('role');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    panel.setAttribute('aria-label', panel.querySelector('h1,h2,h3')?.textContent || 'Detay');
    panel.tabIndex = -1;
    if (locks++ === 0) { previousOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden'; }
    const focusables = () => [...panel.querySelectorAll('button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),a[href],[tabindex="0"]')].filter(el => el.getClientRects().length);
    (panel.querySelector('[data-modal-close]:not(:disabled)') || panel).focus({ preventScroll: true });
    const onKey = event => {
      // A nested popup owns its own Escape/Tab handling.
      const dialogs = [...document.querySelectorAll('[role="dialog"],[role="alertdialog"]')];
      if (dialogs.at(-1) !== panel || document.querySelector('[data-radix-popper-content-wrapper]')) return;
      if (event.key === 'Escape') {
        const close = panel.querySelector('[data-modal-close]:not(:disabled)');
        if (close) { event.preventDefault(); event.stopPropagation(); close.click(); }
      }
      if (event.key === 'Tab') {
        const all = focusables(), first = all[0], last = all.at(-1);
        if (!first) { event.preventDefault(); panel.focus(); }
        else if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || document.activeElement === panel)) { event.preventDefault(); first.focus(); }
      }
    };
    panel.addEventListener('keydown', onKey);
    return () => {
      panel.removeEventListener('keydown', onKey);
      if (--locks === 0) document.body.style.overflow = previousOverflow;
      if (oldRole) panel.setAttribute('role', oldRole); else panel.removeAttribute('role');
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, []);
  return <><span ref={anchor} hidden />{sections(children)}</>;
}

// Shared Radix dialogs already provide focus, Escape and scroll locking.
export function DialogSections({ children }) { return sections(children); }
